/**
 * public-url — the single composer for a tenant-scoped public event/registration
 * URL, now backed by the contracted `getTenantPublicConfig` read (kit
 * ee7551b2 / REQ-011). Host resolution is owned by the data layer; the shell
 * global (`__STUDIO_PUBLIC_ROOT__`) fallback is DROPPED per the 2026-07-07
 * geo/settings handoff — `getTenantPublicConfig` is the single source of truth.
 *
 * `loadPublicConfig()` fetches once and caches; `publicHost()`/`publicUrl()`
 * read the cache synchronously (empty until the first load resolves — call
 * `useTenantPublicConfig()` in any screen that renders a public URL so the
 * value fills in). Prefer the contracted `getPublicEventPage().publicUrl` for a
 * specific event's canonical link; this composer is the tenant-level prefix +
 * slug display form.
 */
import { useEffect, useState } from "react";
import { getTenantPublicConfig, type StudioSettingsRequestOptions } from "@v0y4ge/api-client";
import { can, getActiveTenantSlug, onTenantChange, onTenantSwitching } from "../shell/access";

export type PublicHostPrefix = "attend" | "book" | "join" | "meet" | "public" | "onestop";

/**
 * Seed-world tenant id → DEPLOYMENT slug (2026-09-15).
 *
 * The two are not the same string, and the public hosts belong to the second.
 * This deployment is served at `capture` (Studio on `studio.capture.jrni.ai`,
 * so `join.capture.jrni.ai`), while the seed world's id is `capture-university`
 * (the 2026-09-08 rename). Every composed host — and the served `joinHost` the
 * design harness answers with — carried the seed id, so "Copy join link"
 * produced `join.capture-university.jrni.ai`, a host that resolves to nothing.
 * The 2026-09-14 identity ruling (changelog 2026-09-14T1810-cc-report-0057…)
 * settled that the deployment slug is the correct one.
 *
 * Corrected HERE, in the one composer every screen reads, rather than in a
 * harness fetch layer: more than one layer can answer the public-config route,
 * and whichever wins must not change the answer.
 *
 * Delete when the served config returns the deployment's own hosts.
 */
const DEPLOY_SLUG: Record<string, string> = { "capture-university": "capture" };
/** Map a tenant slug to its deployment slug. */
function deploySlug(tenant: string): string {
  return DEPLOY_SLUG[tenant] ?? tenant;
}
/** Rewrite a SERVED host that carries a seed-world id as one of its labels. */
function withDeploySlug(host: string): string {
  if (!host) return host;
  for (const seed of Object.keys(DEPLOY_SLUG)) {
    const slug = DEPLOY_SLUG[seed]!;
    if (host.includes(`.${seed}.`)) return host.replace(`.${seed}.`, `.${slug}.`);
    if (host === seed) return slug;
    if (host.startsWith(`${seed}.`)) return `${slug}.${host.slice(seed.length + 1)}`;
    if (host.endsWith(`.${seed}`)) return `${host.slice(0, -seed.length)}${slug}`;
  }
  return host;
}

interface PublicConfig {
  publicHost: string;
  attendHost: string;
  /**
   * The queue/walk-in surface's own authority, resolved server-side by the
   * canonical `publicBaseUrlFor(queue)` — so a tenant's custom join domain and
   * a local `localhost:18812` both arrive here WHOLE, port included.
   *
   * OPTIONAL because a deployment that has not configured a join surface has
   * none. There is deliberately no composed substitute: see `publicHost`.
   */
  joinHost?: string;
  /**
   * The One-Stop portal surface's own authority, resolved server-side by the
   * canonical `publicBaseUrlFor(portal)` — so a tenant's custom portal domain
   * and a port arrive here WHOLE, exactly as `joinHost` does.
   *
   * OPTIONAL, and unlike `joinHost` it is genuinely COMPOSABLE when absent:
   * `onestop` is a fixed prefix on the tenant's own domain, so the generic
   * `<prefix>.<tenant>.<rootDomain>` branch in `publicHost` is already right
   * for a stock deployment. The served value is still preferred, because only
   * it can know a custom domain or a port.
   */
  onestopHost?: string;
  rootDomain: string;
  prefixes?: string[];
}
const OPTS: StudioSettingsRequestOptions = {
  baseUrl: (typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_SETTINGS_API_URL) || "/api",
  surface: "settings",
};

let _cfg: PublicConfig | null = null;
let _loading = false;
const _subs = new Set<() => void>();
const _notify = () => _subs.forEach((f) => f());
onTenantSwitching(() => { _cfg = null; _loading = false; _notify(); });
onTenantChange(() => { if (can("settings.read")) loadPublicConfig(); }); // admin/settings/public-config — gated (docket 2026-09-17); screens that render a public URL still warm it on demand via useTenantPublicConfig()

/** Fetch the tenant public config once (idempotent); notifies subscribers on resolve. */
export function loadPublicConfig(): void {
  if (_cfg || _loading || typeof getTenantPublicConfig !== "function") return;
  _loading = true;
  (getTenantPublicConfig as (o: StudioSettingsRequestOptions) => Promise<unknown>)(OPTS)
    .then((r: unknown) => { const raw = r as Record<string, any>; _cfg = (raw?.data ?? raw) as PublicConfig; _notify(); })
    .catch((e: unknown) => console.warn("getTenantPublicConfig failed:", e))
    .finally(() => { _loading = false; });
}

/** React hook: ensures the config is loaded and re-renders when it arrives. */
export function useTenantPublicConfig(): PublicConfig | null {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    _subs.add(fn);
    loadPublicConfig();
    return () => { _subs.delete(fn); };
  }, []);
  return _cfg;
}

export function slugify(s: string): string {
  return (s || "").toString().toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}

/** Awaitable, time-bounded version of loadPublicConfig — for callers that are
 *  about to COMPOSE a fallback URL outside React (no ancestor has warmed the
 *  cache via useTenantPublicConfig). Resolves as soon as the config lands, or
 *  after `timeoutMs` with whatever is cached (possibly null) — the composer
 *  then degrades exactly as before. Fixes the smoke-caught race where a popup
 *  briefly landed on `attend.your-workspace/<slug>` (2026-07-09 amendment). */
export function ensurePublicConfig(timeoutMs = 3000): Promise<PublicConfig | null> {
  if (_cfg) return Promise.resolve(_cfg);
  loadPublicConfig();
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const done = () => { _subs.delete(fn); if (timer) clearTimeout(timer); resolve(_cfg); };
    const fn = () => done();
    _subs.add(fn);
    timer = setTimeout(done, timeoutMs);
  });
}

/** The tenant-scoped public host, e.g. "attend.acme-live.v0y4ge.com".
 *  Sourced from the contracted config; composed from rootDomain + slug only if
 *  the config hasn't loaded yet (returns a bare `prefix.slug` when neither is
 *  available — never a hardcoded vanity domain). */
export function publicHost(prefix: PublicHostPrefix = "attend", tenantSlug?: string | null): string {
  loadPublicConfig();
  /**
   * `join` is SERVED, never composed. It is the only prefix whose authority
   * comes from one field and nothing else.
   *
   * The composed branch below produces `<prefix>.<tenant>.<rootDomain>`, which
   * for a queue meant `join.<tenant>.<rootDomain>` — a hostname that resolves
   * to nothing, while the tenant's real walk-in surface was on a local
   * `localhost:<port>`. A published Walk-Ins link pointed at the invented
   * one. `publicBaseUrlFor(queue)` already knows the answer,
   * custom hosts and ports included, and the backend now returns it as
   * `joinHost`.
   *
   * Absent — the config has not resolved, or the deployment serves no join
   * surface — this returns "". Callers gate on the empty string and show
   * nothing rather than a link that goes nowhere. That is the whole point: an
   * unresolved authority must not be guessed.
   */
  if (prefix === "join") return withDeploySlug((_cfg && _cfg.joinHost) || "");
  /**
   * `onestop` PREFERS the served host and otherwise FALLS THROUGH — the one
   * place this resolver deliberately differs from `join` above.
   *
   * Only the served `onestopHost` can know a custom portal domain or a port.
   * But when it is absent — an events worker older than this bundle, or a
   * deployment that serves no portal surface — the composed
   * `onestop.<tenant>.<rootDomain>` below is CORRECT for a stock tenant,
   * because `onestop` is a fixed prefix on the tenant's own domain. So unlike
   * `join` there is no reason to return "" here, and doing so would blank a
   * link that works today.
   */
  if (prefix === "onestop" && _cfg && _cfg.onestopHost) return withDeploySlug(_cfg.onestopHost);
  if (_cfg) {
    if (prefix === "attend" && _cfg.attendHost) return withDeploySlug(_cfg.attendHost);
    if (_cfg.rootDomain) {
      const t = deploySlug((tenantSlug ?? getActiveTenantSlug()) || "");
      return t ? `${prefix}.${t}.${_cfg.rootDomain}` : withDeploySlug(_cfg.publicHost || `${prefix}.${_cfg.rootDomain}`);
    }
    if (prefix === "attend" && _cfg.publicHost) return withDeploySlug(_cfg.publicHost);
  }
  const t = deploySlug((tenantSlug ?? getActiveTenantSlug()) || "your-workspace");
  return `${prefix}.${t}`;
}

/** Full public URL for an event/page slug. `bare` omits the scheme for display chrome. */
export function publicUrl(slug: string, opts?: { prefix?: PublicHostPrefix; tenantSlug?: string | null; bare?: boolean }): string {
  const host = publicHost(opts?.prefix ?? "attend", opts?.tenantSlug);
  const path = `${host}/${slug || "event"}`;
  return opts?.bare ? path : `https://${path}`;
}

/** Compose from a display title (slugifies first). */
export function publicUrlForTitle(title: string, opts?: { prefix?: PublicHostPrefix; tenantSlug?: string | null; bare?: boolean }): string {
  return publicUrl(slugify(title), opts);
}
